// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { AlertService } from '@app/core/services/alert.service';
import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { PolicyDetailsFacadeService } from '../../state/policy-details/policy-details.facade';
import { INPUT_TEXT_REQUIRED_ERROR, DESCRIPTION_PATTERN,
  NAME_PATTERN_ERROR, DESCRIPTION_PATTERN_ERROR, NAME_PATTERN } from '@app/core/services/input-text-validator.service';
import { PolicyService, readParameterFromRouterState, GoAction } from '@app/core';
import { Store } from '@ngrx/store';
import { LoadingPolicyAction } from '../../state';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-policy-edit',
  templateUrl: './policy-edit.component.html',
  styleUrls: ['./policy-edit.component.scss']
})
export class PolicyEditComponent implements OnDestroy {

  _policyId = '';
  policyForm: FormGroup = new FormGroup({});
  policy$: Observable<any>;
  policyNameControl: FormControl = new FormControl();
  policyDescriptionControl: FormControl = new FormControl();

  nameControlErrors = [
    INPUT_TEXT_REQUIRED_ERROR,
    NAME_PATTERN_ERROR
  ];

  descriptionControlErrors = [
    INPUT_TEXT_REQUIRED_ERROR,
    DESCRIPTION_PATTERN_ERROR,
  ];

  destroy$: Subject<void> = new Subject();

  constructor(
    private store: Store<any>,
    private policyService: PolicyService,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private policyDetailsFacade: PolicyDetailsFacadeService,
    private activatedRoute: ActivatedRoute
  ) {

    this.verifyRouteId();

    this.policyDetailsFacade.initialize(this._policyId);
    this.policy$ = this.policyDetailsFacade.policy$;

    this.loadForm();
  }

  verifyRouteId() {
    const policyId = readParameterFromRouterState(this.activatedRoute, 'id');
    if (!policyId) {
      throw new Error('policy id not in route');
    }
    this._policyId = policyId;
  }

  loadForm () {

    this.policyForm = this.buildPolicyForm();
    this.policyNameControl = this.policyForm.get('policyName') as FormControl;
    this.policyDescriptionControl = this.policyForm.get('policyDescription') as FormControl;

    this.policy$.pipe(takeUntil(this.destroy$)).subscribe((policy: any) => {
      this.policyNameControl.setValue(policy.name);
      this.policyDescriptionControl.setValue(policy.description);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  submit() {
    if (!this._policyId) {
      return;
    }

    if (!this.policyForm.valid) {
      return;
    }

    const request = {
      name: this.policyNameControl.value,
      description: this.policyDescriptionControl.value
    };

    this.policyService.updatePolicy(this._policyId, request).pipe(take(1)).subscribe(() => {
      this.store.dispatch(new GoAction(['/iam/policies']));
      this.store.dispatch(new LoadingPolicyAction());
      this.alertService.success('Policy has successfully been updated.');
    }, () => {
      this.alertService.error('There was an error updating the policy.');
    });
  }

  private buildPolicyForm() {
    return this.formBuilder.group({
      policyName: ['', [ Validators.required, Validators.pattern(NAME_PATTERN)]],
      policyDescription: ['', [ Validators.required, Validators.pattern(DESCRIPTION_PATTERN)]]
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/policies/policy-edit/policy-edit.component.ts